/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UnitSimpleDTO } from '../unit-simple-dto.model';
import { AeroFormComponent } from '../../../aero/aero-form/aero-form.component';
import { ResumeSection } from '../../section/section.model';

/**
 * Angular component that houses rendering the users published content in the content-selection-modal view.
 */
@Component({
  selector: 'bulb-resume-content-selection-display',
  templateUrl: './content-selection-display.component.html',
  standalone: false,
})
export class ResumeContentSelectionDisplayComponent implements OnInit {
  /**
   * User's published units.
   */
  @Input() units : Array<UnitSimpleDTO> = new Array< UnitSimpleDTO >();

  /**
   * Optional input property that is a reference to the aero-form component.
   */
  @Input() aeroFormComponent ?: AeroFormComponent;

  /**
   * Optional input property that is a reference to the potential current resume sub-section.
   * About-me, experience, education, custom, etc...
   */
  @Input() resumeSubSection ?: any;

  /**
   * Boolean indicating whether or not the tile is being rendered in the edit mode of the resume section.
   */
  @Input() editMode ?: boolean = false;

  /**
   * Boolean indicating if the default display for tiles are to be used.
   *
   * Resume sub sections use a different display layout for tiles.
   */
  @Input() renderResumeSubSectionTileView : boolean = false;

  /**
   * Set the value as true if the unit is used for selection.
   */
  @Input() selectionMode ?: boolean = false;

  /**
   * Output property to communicate selected units to the parent component.
   */
  @Output() onUnitSelected : EventEmitter< any > = new EventEmitter<any>();

  /**
   * Output property to communicate removed units to the parent component.
   */
  @Output() onUnitRemoved : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Reference to the selected units the user picked.
   */
  private selectedUnits : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();

  /**
   * Array of collections to build the breadcrumb list.
   */
  public selectedCollection : UnitSimpleDTO;

  /**
   * Default Constructor.
   */
  constructor() {}

  /**
   * @inheritDoc
   */
  ngOnInit() {}

  /**
   * Method that handles selecting a unit from the list and populating the units array.
   *
   * @param event
   * @param unit current unit selected.
   * @param resumeSubSectionView whether or not tiles are being rendered in the resume sub section view.
   */
  selectUnit(event :any, unit : any, resumeSubSectionView ?: boolean) : void {
    if (!resumeSubSectionView) {
      const referenceUnit = jQuery(`#${unit.id}.content-selection-modal-tile`);
      const className = 'unit-selected';
      if (!referenceUnit.hasClass(className)) {
        // Visually indicate currently selected unit :
        referenceUnit.addClass(className);
        switch (unit.unitType) {
          case 'CompositeUnit':
            if (event.entireCollection) {
              // Add whole collection
              this.selectedUnits.push(unit);
            }
            // Specific collection content-selection:
            else {
              // Update units reference, see user is 'drilling' down into a collection.
              this.units = unit.children;
              // Update selected Unit UI state:
              this.updateSelectedUnitsUI();
              this.updateBreadCrumbTrail(event, unit);
            }
            break;
          case 'PublishedPageUnit':
            this.selectedUnits.push(unit);
            break;
        }
      } else {
        referenceUnit.removeClass(className);
        this.removeUnit(unit);
      }
      this.onUnitSelected.emit({ units: this.selectedUnits });
    }
  }

  /**
   * Removes the specific unit from the array of units the user has selected.
   *
   * @param unit
   */
  private removeUnit(unit: any): void {
    this.findAndRemoveUnit(unit);
  }

  /**
   * Method that updates the breadcrumb trail if the user selected a collection 'choose content' option.
   *
   * @param event
   * @param unit
   */
  private updateBreadCrumbTrail(event : any, unit : UnitSimpleDTO) : void {
    this.selectedCollection = unit;
  }

  /**
   * Method that handles setting the correct units on the user's navigation on the breadcrumb trail.
   *
   * @param event
   */
  public handleNavigatingBreadCrumb(event : any) : void {
    if (event.showAllOriginalContent) {
      this.units = event.originalUnits;
    } else {
      this.units = event.units;
    }
    this.updateSelectedUnitsUI();
    this.selectedCollection = null;
  }

  /**
   * Helper method to handle updating the selected units UI indicating to the user their currently selected units.
   * This is useful if the user drills down into (many) collections, and they navigate back up.
   */
  private updateSelectedUnitsUI() : void {
    // Timeout needed, since the view has potentially not fully rendered and initialized during breadcrumb navigation.
    setTimeout(() => {
      const selectedClassName = 'unit-selected';
      this.selectedUnits.forEach((unit) => {
        const referenceUnit = jQuery(`#${unit.id}.content-selection-modal-tile`);
        referenceUnit.addClass(selectedClassName);
      });
    });
  }

  /**
   * Helper method to locate the index of the unit to remove. Handles both published pages and collections.
   *
   * @param unit
   */
  private findAndRemoveUnit(unit : UnitSimpleDTO) : void {
    const index = this.selectedUnits.map((unit : UnitSimpleDTO) => unit.id).indexOf(unit.id);
    if (index > -1) {
      this.selectedUnits.splice(index, 1);
    } else {
      console.error(`Problem removing item : ${this.selectedUnits[index]}, from the selected units array.`);
    }
  }

  /**
   * Helper method to locate the index of the selected portfolio content to remove.
   *
   * @param unit
   */
  private findAndRemovePortfolioContent(unit : UnitSimpleDTO) : void {
    const index = this.units.map((unit : UnitSimpleDTO) => unit.id).indexOf(unit.id);
    if (index > -1) {
      this.units.splice(index, 1);
      this.onUnitRemoved.emit();
      // @ts-ignore not a property of the ResumeSection class, however it is within the component.
      this.resumeSubSection.numberOfRemovedContent++;
      // @ts-ignore not a property of the ResumeSection class, however it is within the component.
      this.resumeSubSection.removedPortfolioContent.push(unit);
    } else {
      console.error(`Problem removing item : ${this.units[index]}, from the units array.`);
    }
  }

  /**
   * Method that handles removing the selected portfolio content from the portfolio content array.
   *
   * @param event
   */
  public handleRemovalOfPortfolioContent(event : any) : void {
    if (event.removePortfolioContent) {
      this.findAndRemovePortfolioContent(event.unit);
    }
  }
}
